import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { FileText, ArrowRight, Loader2 } from "lucide-react";

const DEFAULT_LOGO = "/logo-qasesores.png";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    login_title: "Software de Gestión Empresarial",
    login_title_highlight: "",
    login_subtitle: "Todo gestionalo desde un solo lugar.",
    login_copyright: "© 2026 QAsesores. Corredores de Seguros y Reaseguros SRL.",
    login_logo_url: DEFAULT_LOGO,
    login_show_features: true,
    login_feature1_text: "PDF & DOC",
    login_feature2_text: "Gestión Total",
    login_background_color: "#E8E6E6",
    primary_color: "#0C314C",
    accent_color: "#45E69A",
    show_emergent_badge: false
  });
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from;

  // Load personalization settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await axios.get(`${API}/config/personalization`);
        setSettings(prev => ({ ...prev, ...res.data }));
      } catch (err) {
        console.log("Using default settings");
      }
    };
    loadSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Por favor completa todos los campos");
      return;
    }
    
    setLoading(true);
    try {
      const response = await login(email, password);
      toast.success("Bienvenido de vuelta!");
      // Redirect to pending URL (from email link), user's home page, or dashboard
      if (redirectTo && redirectTo !== "/login") {
        navigate(redirectTo);
      } else {
        const homePage = response.user?.home_page;
        navigate(homePage || "/");
      }
    } catch (err) {
      let msg = "Error al iniciar sesión";
      if (err.response?.data?.detail) {
        msg = err.response.data.detail;
      } else if (err.code === "ERR_NETWORK" || !err.response) {
        msg = "Error de conexión. Verifique su internet e intente de nuevo.";
      } else if (err.message) {
        msg = err.message;
      }
      toast.error(msg);
      console.error("Login error:", err);
    } finally {
      setLoading(false);
    }
  };

  const logoUrl = settings.login_logo_url || DEFAULT_LOGO;

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding (wider: 2/3) */}
      <div 
        className="hidden lg:flex lg:w-2/3 p-12 flex-col justify-between"
        style={{ backgroundColor: settings.login_background_color }}
      >
        <div>
          <img 
            src={logoUrl} 
            alt="Logo" 
            className="h-72 w-auto"
            data-testid="qasesores-logo"
          />
        </div>
        
        <div className="space-y-6">
          <h1 
            className="text-4xl md:text-5xl font-bold font-['Manrope'] tracking-tight leading-tight"
            style={{ color: settings.primary_color }}
          >
            {settings.login_title}{" "}
            {settings.login_title_highlight && (
              <span style={{ color: settings.accent_color }}>
                {settings.login_title_highlight}
              </span>
            )}
          </h1>
          <p className="text-lg text-slate-700 leading-relaxed max-w-md">
            {settings.login_subtitle}
          </p>
          
          {settings.login_show_features && (
            <div className="flex items-center gap-4 pt-4">
              <div className="flex items-center gap-2 text-slate-700">
                <FileText className="w-5 h-5" style={{ color: settings.accent_color }} />
                <span className="text-sm">{settings.login_feature1_text}</span>
              </div>
              <div className="w-px h-4 bg-slate-400"></div>
              <div className="flex items-center gap-2 text-slate-700">
                <div 
                  className="w-5 h-5 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: settings.accent_color }}
                >
                  <span style={{ color: settings.primary_color }} className="text-xs font-bold">✓</span>
                </div>
                <span className="text-sm">{settings.login_feature2_text}</span>
              </div>
            </div>
          )}
        </div>
        
        <p className="text-sm text-slate-600">
          {settings.login_copyright}
        </p>
      </div>

      {/* Right side - Login Form (narrower: 1/3) */}
      <div className="w-full lg:w-1/3 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img 
              src={logoUrl} 
              alt="Logo" 
              className="h-32 w-auto"
            />
          </div>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="space-y-1">
              <CardTitle 
                className="text-2xl font-bold font-['Manrope']"
                style={{ color: settings.primary_color }}
              >
                Iniciar Sesión
              </CardTitle>
              <CardDescription className="text-slate-500">
                Ingresa tus credenciales para acceder a tu cuenta
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="focus:ring-[#0C314C] focus:border-[#0C314C]"
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700">Contraseña</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="focus:ring-[#0C314C] focus:border-[#0C314C]"
                    data-testid="login-password-input"
                  />
                  <div className="flex justify-end">
                    <Link
                      to="/forgot-password"
                      className="text-sm text-slate-500 hover:text-[#0C314C]"
                      data-testid="forgot-password-link"
                    >
                      ¿Olvidaste tu contraseña?
                    </Link>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full text-white font-medium"
                  style={{ backgroundColor: settings.primary_color }}
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <ArrowRight className="w-4 h-4 mr-2" />
                  )}
                  {loading ? "Iniciando..." : "Iniciar Sesión"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
